import routes from '@/Router/routes.constants'

import {
  breakdownableConcept,
  conceptAmountErrors,
  conceptCategories,
  expandableConcepts,
  shippingConcept,
  statuses,
} from '../constants'

export const getItemsToRejectAmount = itemsToReject => {
  if (itemsToReject) {
    return itemsToReject.reduce((prev, next) => prev + Number(next.price), 0)
  }
  return 0
}

export const getFromExchangeOrderData = fromExchangeOrder => {
  if (fromExchangeOrder) {
    return {
      ...fromExchangeOrder,
      returnUrl: `${routes.transactionDetailPage.replace(':id', fromExchangeOrder.return_id)}`,
    }
  }

  return null
}

export const getTransactionStore = (stores = [], transactionStoreId) => {
  return stores?.find(store => store.id === transactionStoreId) || null
}

export const getConceptId = concept => concept.concept || `${concept.type}|${concept.label}`

export const isFeeConcept = concept => concept.category === conceptCategories.fee

export const isExpandableConcept = concept => expandableConcepts.includes(concept.concept)

export const isBreakdownableConcept = concept => concept.concept === breakdownableConcept

export const getSignedAmount = concept => {
  const amount = parseFloat(concept.amount)

  if (isNaN(amount)) return 0
  if (isFeeConcept(concept)) return -Math.abs(amount)

  return amount
}

export const getSumOfSelectedConcepts = selectedConcepts => {
  return selectedConcepts.reduce((acc, concept) => acc + getSignedAmount(concept), 0).toFixed(2)
}

const getItemAmount = item => {
  const amount = parseFloat(item.amount)

  if (isNaN(amount)) return 0

  return amount
}

export const getItemsSum = items => {
  return items.reduce((acc, item) => acc + getItemAmount(item), 0).toFixed(2)
}

const toFixedAmount = amount => {
  const value = parseFloat(amount)

  if (isNaN(value)) return '0.00'

  return value.toFixed(2)
}

const isBreakdownableLineItem = lineItem => {
  if (lineItem.cancelled_from_draft) return false
  if (lineItem.status === statuses.rejected) return false
  if (lineItem.exchanged_for_item) return false
  if (lineItem.exchange_info) return false

  return true
}

const getGroupedLineItems = lineItemGroups => {
  if (!lineItemGroups?.length) return []

  return lineItemGroups.flatMap(group => {
    if (!group.line_items?.length) return []

    return group.line_items
  })
}

const getBreakdownableLineItems = ({ lineItems, lineItemGroups }) => {
  const ungroupedLineItems = lineItems?.length ? lineItems : []

  return [...ungroupedLineItems, ...getGroupedLineItems(lineItemGroups)].filter(isBreakdownableLineItem)
}

const mapConceptItem = lineItem => {
  return {
    id: lineItem.id,
    name: lineItem.product?.name,
    variant: lineItem.variant?.title,
    max_amount: lineItem.price,
    amount: lineItem.price,
  }
}

const getConceptItems = ({ concept, lineItems, lineItemGroups }) => {
  if (!isBreakdownableConcept(concept)) return null

  const breakdownableLineItems = getBreakdownableLineItems({ lineItems, lineItemGroups })

  if (!breakdownableLineItems.length) return null

  const items = breakdownableLineItems.map(mapConceptItem)

  if (getItemsSum(items) !== toFixedAmount(concept.max_amount)) return null

  return items
}

const isConceptSelected = ({ concept, selected, shippingSummaryAmount }) => {
  if (concept.concept === shippingConcept) return shippingSummaryAmount > 0

  return selected
}

const mapRefundConcept = ({ concept, selected, shippingSummaryAmount, lineItems, lineItemGroups }) => {
  return {
    id: getConceptId(concept),
    concept: concept.concept,
    type: concept.type,
    label: concept.label,
    category: concept.category,
    max_amount: concept.max_amount,
    amount: concept.max_amount,
    selected: isConceptSelected({ concept, selected, shippingSummaryAmount }),
    items: getConceptItems({ concept, lineItems, lineItemGroups }),
  }
}

export const buildRefundConcepts = ({
  concepts,
  selected = false,
  shippingSummaryAmount,
  lineItems,
  lineItemGroups,
}) => {
  if (!concepts?.length) return []

  return concepts.map(concept => {
    return mapRefundConcept({ concept, selected, shippingSummaryAmount, lineItems, lineItemGroups })
  })
}

const getAmountError = (amount, maxAmount) => {
  const value = parseFloat(amount)

  if (isNaN(value) || value < 0) return conceptAmountErrors.invalid
  if (value > parseFloat(maxAmount)) return conceptAmountErrors.exceeded

  return null
}

export const getConceptAmountError = concept => {
  if (!concept.selected) return null

  return getAmountError(concept.amount, concept.max_amount)
}

export const getItemAmountError = item => getAmountError(item.amount, item.max_amount)

const hasItemAmountErrors = concept => {
  if (!concept.items?.length) return false

  return concept.items.some(item => getItemAmountError(item))
}

export const hasConceptError = concept => {
  if (!concept.selected) return false
  if (getConceptAmountError(concept)) return true

  return hasItemAmountErrors(concept)
}

export const hasAmountErrors = concepts => {
  if (!concepts?.length) return false

  return concepts.some(hasConceptError)
}

const mapAmountItem = item => ({ id: item.id, amount: toFixedAmount(item.amount) })

const mapAmount = concept => {
  if (!concept.items?.length) return { amount: toFixedAmount(concept.amount) }

  const items = concept.items.map(mapAmountItem).filter(item => parseFloat(item.amount) > 0)

  if (!items.length) return { amount: toFixedAmount(concept.amount) }

  return { amount: getItemsSum(items), items }
}

const mapLabeledAmount = concept => {
  return { type: concept.type, label: concept.label, amount: toFixedAmount(concept.amount) }
}

export const getConceptAmountsPayload = concepts => {
  if (!concepts?.length) return { amounts: {}, labeled_amounts: [] }

  const selectedConcepts = concepts.filter(concept => concept.selected)

  return {
    amounts: Object.fromEntries(
      selectedConcepts.filter(concept => concept.concept).map(concept => [concept.concept, mapAmount(concept)])
    ),
    labeled_amounts: selectedConcepts.filter(concept => concept.type).map(mapLabeledAmount),
  }
}

export const getConceptsWithToggledConcept = (concepts, conceptId) => {
  return concepts.map(concept => {
    if (concept.id !== conceptId) return concept

    return { ...concept, selected: !concept.selected }
  })
}

export const getConceptsWithAmount = (concepts, conceptId, amount) => {
  return concepts.map(concept => {
    if (concept.id !== conceptId) return concept

    return { ...concept, amount }
  })
}

export const getConceptsWithItemAmount = (concepts, conceptId, itemId, amount) => {
  return concepts.map(concept => {
    if (concept.id !== conceptId) return concept

    const items = concept.items.map(item => {
      if (item.id !== itemId) return item

      return { ...item, amount }
    })

    return { ...concept, items, amount: getItemsSum(items) }
  })
}
